import { getAttribution } from "@/domain/attribution/attribution";
import { recordCampaignEngagement, type EngagementStep } from "@/domain/marketing/campaign-engagement";
import { getMerchantId } from "@/lib/company-storage";

import { ensureCartId, readCartId, rotateCartId } from "./cart";
import { claimStorefrontSession } from "./claim";
import { getStorefrontAnalyticsSink } from "./events.sink";
import { classifyBrowser, classifyDeviceClass, classifyPlatform, classifySourceCategory, consumeSessionStartFlag } from "./session";
import type { StorefrontClearReason, StorefrontEventInput } from "./types";

/** Enqueues one storefront analytics event for the current merchant and reports `menu_view`, `add_to_cart` and `checkout_start` as campaign engagement. Never throws; a missing merchant id is a silent no-op. */
export function recordStorefrontEvent(event: StorefrontEventInput): void {
  try {
    const merchantId = getMerchantId();
    if (!merchantId) return;
    getStorefrontAnalyticsSink(merchantId).enqueue(withCart(merchantId, event));
    const step = engagementStepOf(event);
    if (step) recordCampaignEngagement(merchantId, step);
  } catch {
    return;
  }
}

function engagementStepOf(event: StorefrontEventInput): EngagementStep | null {
  switch (event.type) {
    case "menu_view":
      return "visit";
    case "add_to_cart":
      return "add_to_cart";
    case "checkout_start":
      return "checkout_started";
    default:
      return null;
  }
}

/**
 * `session_start`, once per tab session per merchant. Dedupe lives in
 * `sessionStorage` (not the sink's in-memory set) so it survives a page
 * refresh within the same tab, unlike `menu_view`/`item_view`/`checkout_step`.
 */
export function recordStorefrontSessionStart(): void {
  try {
    const merchantId = getMerchantId();
    if (!merchantId) return;
    if (consumeSessionStartFlag(merchantId)) return;

    getStorefrontAnalyticsSink(merchantId).enqueue({
      type: "session_start",
      source: classifySourceCategory(),
      device: classifyDeviceClass(),
      browser: classifyBrowser(),
      platform: classifyPlatform(),
      ...sessionUtm(merchantId),
    });
    void claimStorefrontSession();
  } catch {
    return;
  }
}

/** `order_placed`, flushed immediately so the request survives the redirect to the confirmation page. */
export function recordStorefrontOrderPlaced(orderId: string): void {
  try {
    const merchantId = getMerchantId();
    if (!merchantId) return;
    const sink = getStorefrontAnalyticsSink(merchantId);
    sink.enqueue(withCart(merchantId, { type: "order_placed", orderId }));
    rotateCartId(merchantId);
    void sink.flush();
  } catch {
    return;
  }
}

/** Empties the open cart in analytics and starts a new cart id. A placed order rotates inside `recordStorefrontOrderPlaced`. */
export function recordStorefrontCartCleared(reason: Exclude<StorefrontClearReason, "order">): void {
  try {
    const merchantId = getMerchantId();
    if (!merchantId) return;
    const cartId = readCartId(merchantId);
    if (!cartId) return;
    getStorefrontAnalyticsSink(merchantId).enqueue({ type: "cart_cleared", cartId, clearReason: reason });
    rotateCartId(merchantId);
    void getStorefrontAnalyticsSink(merchantId).flush();
  } catch {
    return;
  }
}

function withCart(merchantId: string, event: StorefrontEventInput): StorefrontEventInput {
  switch (event.type) {
    case "add_to_cart":
      return { ...event, cartId: event.cartId || ensureCartId(merchantId) };
    case "remove_from_cart":
    case "cart_view":
    case "checkout_start":
    case "checkout_step":
    case "order_placed":
    case "heartbeat":
      return { ...event, cartId: event.cartId || readCartId(merchantId) || undefined };
    default:
      return event;
  }
}

function sessionUtm(merchantId: string): { utmSource?: string; utmMedium?: string; utmCampaign?: string } {
  const touch = getAttribution(merchantId);
  if (!touch) return {};
  return {
    ...(touch.utmSource ? { utmSource: touch.utmSource } : {}),
    ...(touch.utmMedium ? { utmMedium: touch.utmMedium } : {}),
    ...(touch.utmCampaign ? { utmCampaign: touch.utmCampaign } : {}),
  };
}
